import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReports } from '../hooks/useReports'
import { DayPicker } from '../components/DayPicker'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorAlert } from '../components/ErrorAlert'

export function EditReport() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { loading, error, getReport, updateReport, deleteReport } = useReports()

  const [report, setReport] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    status: 'active',
    days_of_week: []
  })
  const [formErrors, setFormErrors] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    loadReport()
  }, [id])

  async function loadReport() {
    try {
      const data = await getReport(id)
      setReport(data)
      setFormData({
        name: data.name,
        prompt: data.prompt,
        status: data.status,
        days_of_week: data.schedule_days || []
      })
    } catch (err) {
      console.error('Failed to load report:', err)
    }
  }

  function validateForm() {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = 'Report name is required'
    }

    if (!formData.prompt.trim()) {
      errors.prompt = 'Prompt is required'
    } else if (formData.prompt.length < 20) {
      errors.prompt = 'Prompt must be at least 20 characters'
    }

    if (formData.days_of_week.length === 0) {
      errors.days_of_week = 'Select at least one day'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await updateReport(id, formData)
      navigate('/')
    } catch (err) {
      console.error('Failed to update report:', err)
    }
  }

  async function handleDelete() {
    try {
      await deleteReport(id)
      navigate('/')
    } catch (err) {
      console.error('Failed to delete report:', err)
    }
  }

  if (loading && !report) {
    return <LoadingSpinner message="Loading report..." />
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Report not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Report</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
          Update your report settings
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorAlert message={error} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        {/* Report Name */}
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Report Name {formErrors.name && <span className="text-red-600 text-sm">*</span>}
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
          )}
        </div>

        {/* Status */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="active"
                checked={formData.status === 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="paused"
                checked={formData.status === 'paused'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Paused</span>
            </label>
          </div>
        </div>

        {/* Prompt */}
        <div className="mb-6">
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
            Report Prompt {formErrors.prompt && <span className="text-red-600 text-sm">*</span>}
          </label>
          <textarea
            id="prompt"
            value={formData.prompt}
            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
            rows={6}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.prompt ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {formErrors.prompt && (
            <p className="mt-1 text-sm text-red-600">{formErrors.prompt}</p>
          )}
        </div>

        {/* Day Picker */}
        <div className="mb-6">
          <DayPicker
            selectedDays={formData.days_of_week}
            onChange={(days) => setFormData({ ...formData, days_of_week: days })}
            error={formErrors.days_of_week}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-800 font-medium py-2 text-sm sm:text-base"
          >
            Delete Report
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-gray-700 hover:text-gray-900 font-medium text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Report?</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              This will permanently delete this report and all its execution history. This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-3 sm:py-2 text-gray-700 hover:text-gray-900 font-medium text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 sm:py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
